import React from "react";
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableSortLabel,
    Typography,
    Tooltip
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import CurrencyFormat from "react-currency-format";
import { getCurrencySymbol } from "../utils";

const COLUMNS = [
    { key: "active", label: "", sortable: false },
    { key: "serviceName", label: "Service", sortable: true },
    { key: "desc", label: "Description", sortable: true },
    { key: "quantity", label: "Quantity", sortable: true },
    { key: "packagetype", label: "Package type", sortable: true },
    { key: "price", label: "Price / Discount", sortable: true }
];

export default function PackageTable({
    packages = [],
    sortBy,
    sortDirection,
    onSort,
    onEditPackage
}) {
    return (
        <TableContainer sx={{ tableLayout: "fixed", width: "100%" }}>
            <Table>
                <PackageTableHead
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={onSort}
                />
                <TableBody>
                    {packages.map((pkg) => (
                        <PackageRow
                            key={pkg.id}
                            pkg={pkg}
                            onEditPackage={onEditPackage}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function PackageTableHead({ sortBy, sortDirection, onSort }) {
    return (
        <TableHead>
            <TableRow>
                {COLUMNS.map(({ key, label, sortable }) => (
                    <TableCell
                        key={key}
                        sx={{
                            textWrap: "auto",
                            textAlign:
                                key === "quantity"
                                    ? "center"
                                    : key === "price"
                                      ? "right"
                                      : "left",
                            "&:last-child": {
                                padding: "7px 5px 7px 9px !important"
                            }
                        }}
                    >
                        {sortable ? (
                            <TableSortLabel
                                active={sortBy === key}
                                direction={sortDirection?.toLowerCase()}
                                onClick={() => onSort(key)}
                            >
                                {label}
                            </TableSortLabel>
                        ) : (
                            label
                        )}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

function PackageRow({ pkg, onEditPackage }) {
    const theme = useTheme();
    const description = pkg.desc || "";

    return (
        <TableRow hover onClick={() => onEditPackage(pkg.id, pkg.servicetype?.id)}>
            <TableCell
                sx={{
                    width: "1%",
                    textAlign: "center"
                }}
            >
                <Tooltip
                    componentsProps={{
                        tooltip: {
                            sx: {
                                fontWeight: 400,
                                fontSize: "14px !important"
                            }
                        }
                    }}
                    title={pkg.active ? "Active" : "Inactive"}
                >
                    <CircleIcon
                        sx={{
                            fontSize: "1.05rem",
                            color: pkg.active
                                ? theme.palette.verified
                                : grey[300]
                        }}
                    />
                </Tooltip>
            </TableCell>
            <TableCell
                sx={{
                    width: "20%",
                    minWidth: "160px",
                    whiteSpace: "normal"
                }}
            >
                <Typography>{pkg.servicetype?.name || ""}</Typography>
            </TableCell>
            <TableCell
                sx={{
                    width: "40%",
                    minWidth: "200px",
                    whiteSpace: "normal"
                }}
            >
                {description.length > 80 ? (
                    <Tooltip title={description}>
                        <Typography sx={{ display: "inline-block" }}>
                            {`${description.substring(0, 80)}...`}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography>{description}</Typography>
                )}
            </TableCell>
            <TableCell
                sx={{
                    textAlign: "center",
                    width: "8%"
                }}
            >
                <Typography
                    sx={{
                        position: "relative",
                        translate: "-14%"
                    }}
                >
                    {pkg.quantity}
                </Typography>
            </TableCell>
            <TableCell
                sx={{
                    width: "12%"
                }}
            >
                <Typography>
                    {pkg.packagetype === "DOLLAR"
                        ? "Fixed price"
                        : pkg.packagetype === "PERCENTAGE"
                          ? "Discount"
                          : pkg.packagetype || ""}
                </Typography>
            </TableCell>
            <TableCell
                sx={{
                    textAlign: "right",
                    width: "10%",
                    translate: "-10%"
                }}
            >
                <Typography>
                    {pkg.packagetype === "DOLLAR" ? (
                        <CurrencyFormat
                            value={pkg.price}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={getCurrencySymbol()}
                            decimalScale={2}
                            fixedDecimalScale={true}
                        />
                    ) : pkg.packagetype === "PERCENTAGE" ? (
                        `${pkg.discount}%`
                    ) : (
                        ""
                    )}
                </Typography>
            </TableCell>
        </TableRow>
    );
}
